import React, { useState } from "react";
import { API_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { Bell, Shield, Trash2, AlertTriangle, Settings as SettingsIcon, X, BellRing, Mail, Smartphone } from "lucide-react";

const Settings = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState({
        email: true,
        push: false,
        marketing: true,
    });
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleNotificationChange = (name) => {
        setNotifications((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirm !== "DELETE") return;

        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/auth/account`, {
                method: "DELETE",
                headers: { Authorization: token },
            });

            if (response.ok) {
                localStorage.removeItem("token");
                localStorage.removeItem("userId");
                navigate("/signin");
            } else {
                alert("ลบบัญชีไม่สำเร็จ");
            }
        } catch (error) {
            console.error("Error deleting account:", error);
            alert("เกิดข้อผิดพลาด กรุณาลองใหม่");
        } finally {
            setDeleting(false);
        }
    };

    const ToggleSwitch = ({ checked, onChange }) => (
        <button
            onClick={onChange}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-red-600" : "bg-gray-200"
                }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${checked ? "translate-x-6" : "translate-x-1"
                    }`}
            />
        </button>
    );

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto space-y-8">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-black rounded-lg">
                        <SettingsIcon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">ตั้งค่า</h1>
                        <p className="text-gray-500">จัดการการตั้งค่าบัญชีของคุณ</p>
                    </div>
                </div>

                {/* Notifications Section */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                        <Bell className="w-5 h-5 text-red-600" />
                        <h2 className="text-lg font-semibold text-gray-900">การแจ้งเตือน</h2>
                    </div>
                    <div className="p-6 space-y-6">
                        {/* Email Notifications */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-red-100 rounded-lg">
                                    <Mail className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">อีเมลแจ้งเตือน</p>
                                    <p className="text-sm text-gray-500">รับข่าวสารและอัปเดตต่างๆ ทางอีเมล</p>
                                </div>
                            </div>
                            <ToggleSwitch
                                checked={notifications.email}
                                onChange={() => handleNotificationChange("email")}
                            />
                        </div>

                        {/* Push Notifications */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-gray-100 rounded-lg">
                                    <BellRing className="w-5 h-5 text-gray-600" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">Push Notification</p>
                                    <p className="text-sm text-gray-500">รับการแจ้งเตือนผ่านเบราว์เซอร์</p>
                                </div>
                            </div>
                            <ToggleSwitch
                                checked={notifications.push}
                                onChange={() => handleNotificationChange("push")}
                            />
                        </div>

                        {/* Marketing */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <Smartphone className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900">ข่าวสารการตลาด</p>
                                    <p className="text-sm text-gray-500">รับโปรโมชั่นและข้อเสนอพิเศษ</p>
                                </div>
                            </div>
                            <ToggleSwitch
                                checked={notifications.marketing}
                                onChange={() => handleNotificationChange("marketing")}
                            />
                        </div>
                    </div>
                </div>

                {/* Privacy Section */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                        <Shield className="w-5 h-5 text-green-600" />
                        <h2 className="text-lg font-semibold text-gray-900">ความเป็นส่วนตัว</h2>
                    </div>
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium text-gray-900">สถานะออนไลน์</p>
                                <p className="text-sm text-gray-500">แสดงสถานะเมื่อคุณใช้งาน</p>
                            </div>
                            <ToggleSwitch checked={true} onChange={() => { }} />
                        </div>
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-red-100 bg-red-50 flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <h2 className="text-lg font-semibold text-red-900">พื้นที่อันตราย</h2>
                    </div>
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium text-gray-900">ลบบัญชี</p>
                                <p className="text-sm text-gray-500">การลบบัญชีจะไม่สามารถกู้คืนได้</p>
                            </div>
                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors"
                            >
                                <Trash2 size={18} />
                                ลบบัญชี
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div
                            className="fixed inset-0 bg-black/50"
                            onClick={() => setShowDeleteModal(false)}
                        />

                        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-red-100 rounded-lg">
                                        <AlertTriangle className="w-6 h-6 text-red-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">ลบบัญชี</h3>
                                </div>
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <p className="text-gray-600">
                                    คุณแน่ใจหรือไม่ว่าต้องการลบบัญชี? :
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-500 space-y-1">
                                    <li>ข้อมูลประวัติการสั่งซื้อจะถูกลบ</li>
                                    <li>ที่อยู่จัดส่งจะถูกลบ</li>
                                    <li>ไม่สามารถเข้าสู่ระบบได้อีก</li>
                                    <li>ข้อมูลร้านค้า (ถ้ามี) จะถูกปิด</li>
                                </ul>

                                <div className="pt-4 border-t border-gray-100">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        พิมพ์ <span className="text-red-600 font-bold">DELETE</span> เพื่อยืนยัน
                                    </label>
                                    <input
                                        type="text"
                                        value={deleteConfirm}
                                        onChange={(e) => setDeleteConfirm(e.target.value)}
                                        placeholder="DELETE"
                                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={() => setShowDeleteModal(false)}
                                        className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                                    >
                                        ยกเลิก
                                    </button>
                                    <button
                                        onClick={handleDeleteAccount}
                                        disabled={deleteConfirm !== "DELETE" || deleting}
                                        className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {deleting ? (
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                <Trash2 size={18} />
                                                ลบบัญชี
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
